import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// In-memory cache keyed by normalised URL — dedupes concurrent fetches
// across every component that calls the hook during a session. Reset on
// full reload; the backend still caches for 7 days in Mongo.
const _cache = new Map();
const _inflight = new Map();

const _normalise = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  let url = raw.trim();
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
};

/**
 * Fetch OG/link-preview metadata (title, description, image, favicon,
 * site_name) for a website URL. Values are cached in-memory for the tab
 * session and in Mongo for 7 days on the backend.
 *
 * @returns { data: {title, description, image, favicon, site_name} | null,
 *            loading: boolean, error: string | null }
 */
export function useLinkPreview(website) {
  const normalised = _normalise(website);
  const [state, setState] = useState(() => ({
    data: normalised ? _cache.get(normalised) || null : null,
    loading: !!normalised && !_cache.has(normalised),
    error: null,
  }));
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!normalised) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    if (_cache.has(normalised)) {
      setState({ data: _cache.get(normalised), loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));

    let promise = _inflight.get(normalised);
    if (!promise) {
      promise = axios
        .get(`${API}/link-preview`, { params: { url: normalised } })
        .then((res) => {
          _cache.set(normalised, res.data);
          return res.data;
        })
        .catch((err) => {
          const message = err?.response?.data?.detail || err.message || 'fetch failed';
          _cache.set(normalised, null);
          throw new Error(message);
        })
        .finally(() => {
          _inflight.delete(normalised);
        });
      _inflight.set(normalised, promise);
    }

    promise
      .then((data) => {
        if (mountedRef.current) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (mountedRef.current) setState({ data: null, loading: false, error: err.message });
      });
  }, [normalised]);

  return state;
}
